/* ================= FIELD LABELS ================= */

const FIELD_LABEL_MAP = {
  teststep: 'Test step',
  attachment: 'Attachment',
  testcase: 'Test case',
  iteration: 'Iteration',
  is_automation_needed: 'Candidate for automation',
  not_automated_category: 'Category (not automated)',
  not_automated_sub_category: 'Subcategory (not automated)',
};

const normalizeFieldKey = (field = '') =>
  field.toLowerCase().replace(/\s+/g, '_').replace(/_+/g, '_');

/* ================= HELPERS ================= */

export const getUserName = (u) =>
  u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : 'Unknown';

export const getRelativeLabel = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return null;
};

/* ================= FIELD FORMAT ================= */

export const formatFieldName = (field = '') => {
  if (field.includes(' · ')) {
    const idx = field.indexOf(' · ');
    const rawKey = field.slice(0, idx);
    const rawField = field.slice(idx + 3);

    const key = rawKey
      .split('-')
      .map(seg => seg.replace(/_/g, '').toUpperCase())
      .filter(Boolean)
      .join('-');

    const suffix = rawField
      .replace(/^_+/, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, c => c.toUpperCase());

    return `${key} · ${suffix}`;
  }

  const normalized = normalizeFieldKey(field);
  if (FIELD_LABEL_MAP[normalized]) return FIELD_LABEL_MAP[normalized];

  return field
    .replace(/^_+/, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, c => c.toUpperCase());
};

/* ================= VALUE FORMAT ================= */

export const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';

  if (typeof v === 'string' && v.includes('@')) {
    const parts = v.split('@');
    const fileName = parts[parts.length - 1];
    if (fileName?.includes('.')) return fileName;
  }

  return String(v);
};


export const processEntries = (entries) => {
  const map = new Map();

  entries.forEach((entry) => {
    if (!entry) return;
    const rawChanges = typeof entry.changes === 'string'
      ? JSON.parse(entry.changes)
      : entry.changes;

    const date = new Date(entry.created_at);
    const minuteKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
    const key = `${entry.performed_by_user?.first_name}-${minuteKey}-${entry.action_type}`;

    const changes = Object.entries(rawChanges || {})
      .map(([field, { old_value, new_value }]) => {
        const oldVal = formatValue(old_value);
        const newVal = formatValue(new_value);

        if (oldVal === newVal) return null;
        if (!oldVal && !newVal) return null;

        const formattedField = formatFieldName(field);
        const normalized = field.includes(' · ') ? field : normalizeFieldKey(field);

        return {
          field: formattedField,
          rawField: normalized,
          isTcField: field.includes(' · '),
          bareField: field.includes(' · ')
            ? field.split(' · ').at(-1)
            : null,
          group: normalized.includes('not_automated') ? 'automation_reason' : 'general',
          old: oldVal,
          new: newVal,
        };
      })
      .filter(Boolean);

    if (!changes.length && entry.action_type === 'UPDATE') return;

    if (!map.has(key)) {
      map.set(key, { ...entry, changes });
    } else {
      map.get(key).changes.push(...changes);
    }
  });

  return Array.from(map.values());
};

/* ================= SUMMARY ================= */

export const buildSummary = (entry) => {
  if (entry.action_type === 'CREATE') {
    const entityLabel = entry.entity_name === 'test_cycle' ? 'test cycle' : 'test case';
    return `created this ${entityLabel}`;
  }
  if (entry.action_type === 'DELETE') return 'removed';

  if (entry.action_type === 'REMOVE') {
    const count = entry.changes.length;
    return count === 1 ? 'removed 1 execution' : `removed ${count} executions`;
  }

  const changes = Array.isArray(entry.changes) ? entry.changes : [];

  if (!changes.length) return 'made updates';

  const isAutomationCandidate = changes.some(
    (c) => c.rawField === 'is_automation_needed'
  );
  if (isAutomationCandidate) return 'marked as candidate for automation';

  const hasAutomationReason = changes.some(
    (c) => c.group === 'automation_reason'
  );
  if (hasAutomationReason) return 'updated reason for not automating';
  const tcFields = changes.filter((c) => c.isTcField);
  if (entry.action_type === 'ASSIGN') {
    const count = tcFields.length || changes.length;
    return count === 1
      ? 'assigned 1 test case'
      : `assigned ${count} test cases`;
  }

  if (tcFields.length > 0 && tcFields.length === changes.length) {
    const bareFields = [...new Set(tcFields.map((c) => c.bareField).filter(Boolean))];
    const count = tcFields.length;
    const fieldLabel = bareFields.length === 1
      ? bareFields[0].toLowerCase()
      : `${bareFields.length} fields`;
    return count === 1
      ? `updated ${fieldLabel} for 1 test case`
      : `updated ${fieldLabel} for ${count} test cases`;
  }

  const hasAdded = changes.some((c) => !c.old && c.new);
  const fields = [...new Set(changes.map((c) => c.field.toLowerCase()))];

  const hasExecutionStarted = changes.some((c) => c.rawField === 'execution_started');
  if (hasExecutionStarted) {
    const count = changes.filter((c) => c.rawField === 'execution_started').length;
    return count === 1 ? 'started execution for 1 test case' : `started execution for ${count} test cases`;
  }

  if (hasAdded) {
    return fields.length <= 3
      ? `added ${fields.join(', ')}`
      : `added ${fields.length} fields`;
  }

  return fields.length <= 3
    ? `updated ${fields.join(', ')}`
    : `updated ${fields.length} fields`;
};